import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  getRegistrationOptions,
  submitRegistration,
  getAuthOptions,
  submitAuthentication,
} from '@/api/webauthn';
import { logout } from '@/api/auth';

interface SessionLockScreenProps {
  /** Whether the lock overlay is currently visible */
  isVisible: boolean;
  /** Whether the browser supports WebAuthn platform authenticators */
  biometricAvailable: boolean | null;
  /** Display name of the current user */
  userName?: string;
  /** Callback invoked on successful unlock */
  onUnlock: () => void;
}

type LockPhase =
  | 'idle'
  | 'checking'
  | 'prompting'
  | 'verifying'
  | 'success'
  | 'no_credentials'
  | 'setup'
  | 'setup_prompting'
  | 'error';

const SessionLockScreen: React.FC<SessionLockScreenProps> = ({
  isVisible,
  biometricAvailable,
  userName,
  onUnlock,
}) => {
  const [phase, setPhase] = useState<LockPhase>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deviceName, setDeviceName] = useState('');

  // Derive a friendly device name once
  useEffect(() => {
    const ua = navigator.userAgent;
    if (/iPhone/i.test(ua)) setDeviceName('iPhone');
    else if (/iPad/i.test(ua)) setDeviceName('iPad');
    else if (/Android/i.test(ua)) setDeviceName('Android device');
    else if (/Mac/i.test(ua)) setDeviceName('Mac');
    else setDeviceName('this device');
  }, []);

  const attemptBiometric = useCallback(async () => {
    setPhase('checking');
    setErrorMessage(null);

    try {
      const options = await getAuthOptions();

      if (options === null) {
        // No credentials registered for this user/device
        setPhase('no_credentials');
        return;
      }

      setPhase('prompting');
      const assertion = await navigator.credentials.get({
        publicKey: {
          ...options,
          userVerification: 'required',
        },
      }) as PublicKeyCredential;

      setPhase('verifying');
      const result = await submitAuthentication(assertion);

      if (result.verified) {
        setPhase('success');
        onUnlock();
      } else {
        setErrorMessage('Verification failed. Please try again.');
        setPhase('error');
      }
    } catch (err: any) {
      // NotAllowedError: user cancelled, timed out, or no passkey on this device
      if (err?.name === 'NotAllowedError') {
        setErrorMessage('No passkey found on this device, or the prompt was dismissed. Try again or set up biometrics on this device.');
      } else {
        setErrorMessage('An error occurred. Please try again or sign out.');
      }
      setPhase('error');
    }
  }, [onUnlock]);

  const attemptSetupAndUnlock = useCallback(async () => {
    setPhase('setup_prompting');
    setErrorMessage(null);

    try {
      const options = await getRegistrationOptions();
      const credential = await navigator.credentials.create({
        publicKey: options,
      }) as PublicKeyCredential;

      const result = await submitRegistration(credential, deviceName);
      if (result.success) {
        // Now authenticate with the just-registered credential
        await attemptBiometric();
      } else {
        setErrorMessage('Failed to set up biometrics. Please sign out and back in.');
        setPhase('error');
      }
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        setErrorMessage('Biometric setup was cancelled.');
      } else {
        setErrorMessage('Could not set up biometrics. Please sign out and try again.');
      }
      setPhase('no_credentials');
    }
  }, [attemptBiometric, deviceName]);

  const handleSignOut = useCallback(async () => {
    await logout();
  }, []);

  // Auto-trigger biometric auth whenever the overlay becomes visible
  useEffect(() => {
    if (!isVisible || biometricAvailable === null) return;
    if (biometricAvailable) {
      attemptBiometric();
    } else {
      setPhase('no_credentials');
    }
    // Intentionally running only when isVisible flips to true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  if (!isVisible) return null;

  const isBusy =
    phase === 'checking' ||
    phase === 'prompting' ||
    phase === 'verifying' ||
    phase === 'setup_prompting';

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-label="Session locked — verify your identity"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: 3,
      }}
    >
      {/* Branding */}
      <Box component="img" src="/images/Bolt.png" alt="Bolt" sx={{ height: 40, mb: 4 }} />

      {/* Lock icon */}
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 36, color: 'primary.contrastText' }} />
      </Box>

      {/* Title */}
      <Typography variant="h6" fontWeight={600} textAlign="center" gutterBottom>
        Session locked
      </Typography>

      {/* Subtitle */}
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
        {userName ? `Welcome back, ${userName.split(' ')[0].replace(/,+$/, '')}.` : 'Welcome back.'}{' '}
        Verify your identity to continue.
      </Typography>

      {/* State-dependent content */}
      {isBusy && (
        <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            {phase === 'setup_prompting'
              ? 'Setting up biometrics…'
              : phase === 'verifying'
              ? 'Verifying…'
              : 'Waiting for biometric prompt…'}
          </Typography>
        </Box>
      )}

      {phase === 'error' && (
        <Box width="100%" maxWidth={360} mb={2}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
          {biometricAvailable && (
            <>
              <Button
                fullWidth
                variant="contained"
                startIcon={<FingerprintIcon />}
                onClick={attemptBiometric}
                sx={{ mb: 1 }}
              >
                Try again
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FingerprintIcon />}
                onClick={attemptSetupAndUnlock}
                sx={{ mb: 1 }}
              >
                Set up on this device
              </Button>
            </>
          )}
        </Box>
      )}

      {phase === 'no_credentials' && (
        <Box width="100%" maxWidth={360} mb={2}>
          {biometricAvailable ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                No biometric is set up for {deviceName}. Set it up now for faster access, or sign
                out and back in.
              </Alert>
              <Button
                fullWidth
                variant="contained"
                startIcon={<FingerprintIcon />}
                onClick={attemptSetupAndUnlock}
                sx={{ mb: 1 }}
              >
                Set up biometrics &amp; unlock
              </Button>
            </>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Biometric authentication is not available on this device. Please sign out and sign
              back in to continue.
            </Alert>
          )}
        </Box>
      )}

      {/* Sign-out fallback — always available when not busy */}
      {!isBusy && phase !== 'success' && (
        <>
          {(phase === 'error' || phase === 'no_credentials') && (
            <Divider sx={{ width: '100%', maxWidth: 360, my: 1 }} />
          )}
          <Button
            variant="text"
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={handleSignOut}
            sx={{ color: 'text.secondary', mt: phase === 'idle' ? 2 : 0 }}
          >
            Sign out
          </Button>
        </>
      )}
    </Box>
  );
};

export default SessionLockScreen;
